import { download, generateCsv, mkConfig } from "export-to-csv"
import { DateTime } from "luxon"
import { useCallback } from "react"
import { GuestInfo } from "../guestListTypes"
import { useDateTime } from "../../../../localization/useDateTime"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"

type useExportOrdersToCsvParams = Readonly<{
    data: GuestInfo[] | null | undefined
    startDate: DateTime
    endDate: DateTime
}>

export function useExportGuests({ data, startDate, endDate }: useExportOrdersToCsvParams) {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ".",
        useBom: true,
        useKeysAsHeaders: true,
        filename: `Facilitynet-guests-${dateTimeFactory.formatDate(startDate)}_${dateTimeFactory.formatDate(endDate)}`,
    })

    const mapGuestsToCsvRow = (guest: GuestInfo) => {
        return {
            From: dateTimeFactory.formatDate(startDate),
            To: dateTimeFactory.formatDate(startDate),
            "Check-in": guest.timeline?.checkInTime
                ? dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(guest.timeline.checkInTime))
                : "",
            Checkout: guest.timeline?.checkOutTime
                ? dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(guest.timeline.checkOutTime))
                : "",
            "Guest name": guest.attendee?.name,
            "Meeting name": guest.order?.meetingDetails?.meetingName,
            "Guest company": guest.attendee?.company,
            "Host name": guest.host?.displayName,
            "Host company": guest.host?.company?.name,
            State: guest.state
                ? l10n.getStringForEnum(
                      "service-center-reports-dashboard-guests-table-host-company-status",
                      guest.state,
                      "Unknown",
                  )
                : "",
        }
    }

    return useCallback(() => {
        let exportRows: ReturnType<typeof mapGuestsToCsvRow>[]

        if (data && data.length > 0) {
            exportRows = data.map((guest) => mapGuestsToCsvRow(guest))
        } else {
            exportRows = [mapGuestsToCsvRow({} as GuestInfo)]
        }

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }, [dateTimeFactory, data, startDate, endDate, l10n])
}
